import React, { useState } from "react";
import {
  Flex,
  Box,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Badge,
  Button,
  Tag,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  MenuDivider,
} from "@chakra-ui/react";
import { useAppContext } from "../../contexts/AppContext";
import { EllipsisVertical } from "lucide-react";

const generateRandomDate = (start, end) => {
  const randomTimestamp =
    start.getTime() + Math.random() * (end.getTime() - start.getTime());
  const randomDate = new Date(randomTimestamp);
  return randomDate.toISOString(); // Return ISO string for formatting later
};

const AllReportsTable = ({ reportManager }) => {
  
  const { data : funds } = useAppContext();
  const { formatDate, tableSize, getFileIcon } = useAppContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Define a range for random dates (e.g., last 3 years)
  const randomDateStart = new Date();
  randomDateStart.setFullYear(randomDateStart.getFullYear() - 3);
  const randomDateEnd = new Date();

  // Aggregate all reports from all funds
  const allReports = funds.flatMap((fund) => {
    const reports = Array.isArray(fund?.factSheet?.reports)
      ? fund.factSheet.reports
      : [];
    return reports.map((report) => ({
      ...report,
      fundName: fund.fundName,
      date: report.date || generateRandomDate(randomDateStart, randomDateEnd), // Assign a random date if none exists
    }));
  });

  // Extraire toutes les catégories disponibles (et s'assurer qu'elles sont uniques)
  const categories = [
    "All",
    ...new Set(allReports.map((report) => report.category)),
  ];

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  // Gère le filtre par catégorie
  const handleFilter = (category) => {
    setSelectedCategory(category);
  };

  // Appliquer les filtres et la recherche
  const filteredReports = allReports.filter((report) => {
    const matchesSearch =
      report.name.toLowerCase().includes(searchTerm) ||
      report.category.toLowerCase().includes(searchTerm) ||
      report.fundName.toLowerCase().includes(searchTerm);

    const matchesCategory =
      selectedCategory === "All" || report.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const handleViewReport = (fund, reportName) => {
    reportManager.openFullScreenReportModal(
      { label: fund }, // fund data
      { label: reportName } 
    );
  };

  return (
    <>
      {/* Barre de recherche */}
      <Flex>
        <Input
          placeholder="Search reports by name, fund, or category..."
          value={searchTerm}
          onChange={handleSearchChange}
          size="lg"
        />
      </Flex>

      <Flex
        id="filters"
        w={{ lg: "fit-content", base: "100vw" }}
        overflowX={"auto"}
        flexWrap={"nowrap"}
        style={{ scrollbarWidth: "none" }}
        mx={{ lg: 0, base: -8 }}
        px={{ lg: 0, base: 8 }}>
        <Flex w={"fit-content"} gap={1} bg={'grey.100'} p={1} borderRadius={8}>
          {categories.map((category) => (
            <Button
            size={"sm"}
            key={category}
            fontWeight={"500"}
            bg={selectedCategory === category ? "white" : "transparent"}
            color={selectedCategory === category ? "brand.700" : "grey.500"}
            _hover={{
              color: selectedCategory === category ? "brand.700" : "grey.700",
            }}
            onClick={() => handleFilter(category)}
            alignItems="center"
            w={"fit-content"}
            cursor={"pointer"}>
            {category}
          </Button>
          
          ))}
        </Flex>
      </Flex>

      {/* Tableau */}
      {filteredReports.length > 0 ? (
        <TableContainer>
          <Table variant="simple" size={tableSize}>
            <Thead>
              <Tr>
                <Th>Report Name</Th>
                <Th>Fund Name</Th>
                <Th>Category</Th>
                <Th>Date</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredReports.map((report, index) => (
                <Tr
                  key={index}
                  onClick={() =>
                    handleViewReport(report.fundName, report.category)
                  }>
                  <Td fontWeight="500">
                    <Flex align="center" gap={4}>
                      <Box w="20px" h="20px">
                        {getFileIcon(report.name)}
                      </Box>
                      {report.name}
                    </Flex>
                  </Td>
                  <Td>
                    <Tag colorScheme="brand">{report.fundName}</Tag>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">{report.category}</Badge>
                  </Td>
                  <Td>
                    <Badge>{formatDate(report.date)}</Badge>
                  </Td>
                  <Td textAlign={"right"}>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        variant={"ghost"}
                        size={"xs"}
                        icon={<EllipsisVertical size={12} />}
                        onClick={(e) => {
                          e.stopPropagation(); // Empêcher la propagation du click
                        }}
                      />
                      <MenuList>
                        <MenuItem>
                          View
                        </MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation(); // Empêcher la propagation du click
                          }}>
                          Download
                        </MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation(); // Empêcher la propagation du click
                          }}>
                          Send by email
                        </MenuItem>
                        <MenuDivider />
                        <MenuItem
                          color={"red.500"}
                          onClick={(e) => {
                            e.stopPropagation(); // Empêcher la propagation du click
                          }}>
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <Box textAlign="center" py={10} color="gray.500">
          No reports found
        </Box>
      )}
    </>
  );
};

export default AllReportsTable;
